import React, { useEffect, useState } from "react";
import {
    Alert,
    Box,
    Button,
    Checkbox,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    Stack,
    TextField,
    Typography,
} from "@mui/material";
import { Search as SearchIcon } from "@mui/icons-material";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";

import { usePersons } from "../../../../hooks/usePersons";
import { useAuthContext } from "../../../../context/AuthProvider";

const schema = yup.object({
    perid: yup.number().nullable(),
    pernom: yup.string().trim().required("El nombre es obligatorio."),
    perape: yup.string().trim().required("El apellido es obligatorio."),
    perdni: yup.string().matches(/^\d{8}$/, "Debe tener 8 dígitos.").required("El DNI es obligatorio."),
    mandetasi: yup.string().nullable(),
    mandetpunsub: yup.string().nullable(),
    mandetpunbaj: yup.string().nullable(),
    mandettaxser: yup.boolean().default(false),
});

const cleanDefaults = (values = {}) => ({
    perid: values.perid || null,
    pernom: values.pernom || "",
    perape: values.perape || "",
    perdni: values.perdni || "",
    mandetasi: values.mandetasi || "",
    mandetpunsub: values.mandetpunsub || "",
    mandetpunbaj: values.mandetpunbaj || "",
    mandettaxser: Boolean(values.mandettaxser),
});

const PassengerForm = ({ open, handleClose, onSubmit, defaultValues = {} }) => {
    const isEditing = Boolean(defaultValues?.mandetid || defaultValues?.perid);
    const { user } = useAuthContext();
    const { createMutation, useFetchPersonsByDNI } = usePersons();

    const [dniToSearch, setDniToSearch] = useState("");
    const [searchDni, setSearchDni] = useState(null);
    const [searchState, setSearchState] = useState("idle");

    const {
        control,
        handleSubmit,
        setValue,
        getValues,
        reset,
        formState: { errors, isValid },
    } = useForm({
        resolver: yupResolver(schema),
        defaultValues: cleanDefaults(defaultValues),
        mode: "onChange",
    });

    const { data: person, isLoading, refetch } = useFetchPersonsByDNI(searchDni, {
        enabled: false,
    });

    useEffect(() => {
        if (!open) return;

        const values = cleanDefaults(defaultValues);
        reset(values);
        setDniToSearch(values.perdni || "");
        setSearchDni(null);
        setSearchState("idle");
    }, [open, defaultValues, reset]);

    const handleSearch = async () => {
        const dni = String(dniToSearch || "").trim();

        if (!/^\d{8}$/.test(dni)) {
            setSearchState("invalid");
            return;
        }

        setSearchDni(dni);
        const result = await refetch();

        if (result?.data) {
            setValue("perid", result.data.perid);
            setValue("pernom", result.data.pernom || "");
            setValue("perape", result.data.perape || "");
            setValue("perdni", dni);
            setSearchState("found");
        } else {
            setValue("perid", null);
            setValue("perdni", dni);
            setValue("pernom", "");
            setValue("perape", "");
            setSearchState("not_found");
        }
    };

    const submitForm = async () => {
        const formData = getValues();

        if (formData.perid || isEditing) {
            onSubmit(formData);
            return;
        }

        createMutation.mutate(
            {
                pernom: formData.pernom,
                perape: formData.perape,
                perdni: formData.perdni,
                crebyid: user.EmpID,
            },
            {
                onSuccess: (createdPerson) => {
                    onSubmit({
                        ...formData,
                        perid: createdPerson?.perid,
                    });
                },
            }
        );
    };

    return (
        <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
            <DialogTitle>
                <Typography variant="h6" fontWeight="bold">
                    {isEditing ? "Editar pasajero" : "Agregar pasajero"}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    Busca por DNI, completa los datos y registra al pasajero en el manifiesto.
                </Typography>
            </DialogTitle>

            <DialogContent dividers>
                <Stack spacing={2}>
                    <Box display="flex" gap={1}>
                        <Controller
                            name="perdni"
                            control={control}
                            render={({ field }) => (
                                <TextField
                                    {...field}
                                    label="DNI"
                                    fullWidth
                                    size="small"
                                    value={field.value || ""}
                                    onChange={(e) => {
                                        const value = e.target.value.replace(/\D/g, "").slice(0, 8);
                                        field.onChange(value);
                                        setDniToSearch(value);
                                        setSearchState("idle");
                                    }}
                                    error={!!errors.perdni}
                                    helperText={errors.perdni?.message || " "}
                                />
                            )}
                        />

                        <Button
                            variant="outlined"
                            startIcon={isLoading ? <CircularProgress size={16} /> : <SearchIcon />}
                            onClick={handleSearch}
                            disabled={isLoading || dniToSearch.length !== 8}
                            sx={{ height: 40, minWidth: 110 }}
                        >
                            Buscar
                        </Button>
                    </Box>

                    {searchState === "found" && (
                        <Alert severity="success" variant="outlined">
                            Persona encontrada. Los datos fueron cargados.
                        </Alert>
                    )}

                    {searchState === "not_found" && (
                        <Alert severity="warning" variant="outlined">
                            Persona no encontrada. Se creará un nuevo registro al guardar.
                        </Alert>
                    )}

                    {searchState === "invalid" && (
                        <Alert severity="error" variant="outlined">
                            El DNI debe tener 8 dígitos.
                        </Alert>
                    )}

                    <Controller
                        name="pernom"
                        control={control}
                        render={({ field }) => (
                            <TextField
                                {...field}
                                label="Nombre"
                                fullWidth
                                size="small"
                                error={!!errors.pernom}
                                helperText={errors.pernom?.message}
                            />
                        )}
                    />

                    <Controller
                        name="perape"
                        control={control}
                        render={({ field }) => (
                            <TextField
                                {...field}
                                label="Apellido"
                                fullWidth
                                size="small"
                                error={!!errors.perape}
                                helperText={errors.perape?.message}
                            />
                        )}
                    />

                    <Controller
                        name="mandetasi"
                        control={control}
                        render={({ field }) => (
                            <TextField {...field} label="Asiento" fullWidth size="small" value={field.value || ""} />
                        )}
                    />

                    <Controller
                        name="mandetpunsub"
                        control={control}
                        render={({ field }) => (
                            <TextField {...field} label="Punto de subida" fullWidth size="small" value={field.value || ""} />
                        )}
                    />

                    <Controller
                        name="mandetpunbaj"
                        control={control}
                        render={({ field }) => (
                            <TextField {...field} label="Punto de bajada" fullWidth size="small" value={field.value || ""} />
                        )}
                    />

                    <Controller
                        name="mandettaxser"
                        control={control}
                        render={({ field }) => (
                            <FormControlLabel
                                control={<Checkbox checked={Boolean(field.value)} onChange={(e) => field.onChange(e.target.checked)} />}
                                label="Requiere servicio de taxi"
                            />
                        )}
                    />
                </Stack>
            </DialogContent>

            <DialogActions>
                <Button onClick={handleClose}>Cancelar</Button>
                <Button
                    variant="contained"
                    onClick={handleSubmit(submitForm)}
                    disabled={createMutation.isPending}
                >
                    {createMutation.isPending ? "Guardando..." : isEditing ? "Guardar" : "Agregar pasajero"}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default PassengerForm;